import { parseDateValue } from '@/services/dateValue';

// The savings band on a policy: what a savings plan (AHORRO) or a
// deposit-at-will plan (INVERSION) is worth, as the carrier last reported it.
//
// The backend sends `savingsDetails` exactly as read from the portal, every
// figure nullable; a figure the carrier did not report is left out rather than
// shown as zero, because "0 withdrawn" and "not reported" are different claims.

export const SAVINGS_INSURANCE_TYPES = ['AHORRO', 'INVERSION'];

/**
 * Whether a savings policy has no instalments to show. Skandia's receipts build
 * an AHORRO / INVERSION schedule; Allianz's payments only mark one that already
 * exists, and nothing builds one, so an Allianz savings policy has none. Its
 * Payments tab would be an empty table, so the tab is left out until a schedule
 * exists — then it shows again by itself.
 */
export function hasNoSavingsPayments(policy) {
  if (!SAVINGS_INSURANCE_TYPES.includes(policy?.insuranceType)) return false;
  return !(policy.paymentDetails?.payments?.length);
}

const MONEY_ROWS = [
  // The balance first: it is the one number a reader opens the policy for.
  { key: 'balance', labelKey: 'policies.savings.Balance', primary: true },
  { key: 'contributed', labelKey: 'policies.savings.Contributed' },
  { key: 'withdrawals', labelKey: 'policies.savings.Withdrawals' },
  { key: 'surrenderValue', labelKey: 'policies.savings.SurrenderValue' },
];

const isPresent = (v) => v !== null && v !== undefined && v !== '';

// A calendar date ('2026-08-31'): parsed with no zone, so no timezone can move its day.
const formatAsOf = (value) => {
  if (!value) return '';
  const parsed = parseDateValue(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : String(value);
};

/**
 * How many contributions are missed, from the carrier's signed count: negative
 * means behind (-42 is 42 missed), zero or positive means up to date. Null when
 * the carrier did not say.
 */
export function missedContributions(pending) {
  if (!isPresent(pending)) return null;
  const n = Number(pending);
  if (!Number.isFinite(n)) return null;
  return n < 0 ? Math.abs(Math.trunc(n)) : 0;
}

/**
 * The rows of the band, in display order, or null when there is nothing to show.
 *
 * Each row is { key, labelKey, kind, value, primary?, warning? }: `money` rows
 * carry the raw number for the caller's currency formatter; the `missed` row
 * carries the count of missed contributions (0 = up to date). `asOf` is the
 * calendar date the figures are from, formatted without any timezone shift.
 */
export function savingsBand(insuranceType, details) {
  if (!SAVINGS_INSURANCE_TYPES.includes(insuranceType) || !details) return null;
  const rows = MONEY_ROWS
    .filter(({ key }) => isPresent(details[key]))
    .map((row) => ({ ...row, kind: 'money', value: Number(details[row.key]) }));
  const missed = missedContributions(details.pendingContributions);
  if (missed !== null) {
    rows.push({
      key: 'pendingContributions',
      labelKey: 'policies.savings.MissedContributions',
      kind: 'missed',
      value: missed,
      warning: missed > 0,
    });
  }
  if (isPresent(details.contribution)) {
    rows.push({
      key: 'contribution',
      labelKey: 'policies.savings.PlanContribution',
      kind: 'money',
      value: Number(details.contribution),
    });
  }
  if (!rows.length) return null;
  return { rows, asOf: formatAsOf(details.asOf) };
}
